import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { ItemV2 } from '@/lib/api/types/items';
import { itemsBulkFetchV2QueryOptions } from '@/lib/queries/common/useGetItemsBulkFetchV2Query';

const CHILDREN_STALE_TIME = 5 * 60 * 1000;
const EMPTY_CHILDREN: ReadonlyMap<number, Partial<ItemV2>[]> = new Map();
const EMPTY_LOADING: ReadonlySet<number> = new Set();

/**
 * Lazily fetch full bodies for the children of parents that are expanded, one
 * query per parent keyed by the parent's child-id list. Keying per parent means
 * a parent's children are fetched once and reused — scrolling a parent out and
 * back is a cache hit, and expanding a new parent fetches only its own children.
 * Fetching is gated on the parent being in the virtualized viewport.
 */
export const useTimelineLazyChildren = ({
  childIdsByParentId,
  expandedChildren,
  visibleParentIds,
  customFieldIds,
}: {
  childIdsByParentId: ReadonlyMap<number, number[]>;
  expandedChildren: Record<number, boolean>;
  visibleParentIds: ReadonlySet<number>;
  customFieldIds?: number[];
}): {
  childrenByParentId: ReadonlyMap<number, Partial<ItemV2>[]>;
  loadingParentIds: ReadonlySet<number>;
  isFetching: boolean;
} => {
  const expandedParents = useMemo(() => {
    const parents: { parentId: number; childIds: number[] }[] = [];
    for (const [parentId, childIds] of childIdsByParentId) {
      if (expandedChildren[parentId]) parents.push({ parentId, childIds });
    }
    return parents;
  }, [childIdsByParentId, expandedChildren]);

  const results = useQueries({
    queries: expandedParents.map(({ parentId, childIds }) =>
      itemsBulkFetchV2QueryOptions(
        { itemIds: childIds, customFieldIds },
        CHILDREN_STALE_TIME,
        visibleParentIds.has(parentId),
      ),
    ),
  });

  const childrenByParentId = useMemo(() => {
    if (expandedParents.length === 0) return EMPTY_CHILDREN;
    const map = new Map<number, Partial<ItemV2>[]>();
    expandedParents.forEach(({ parentId, childIds }, index) => {
      const itemsById = results[index]?.data?.itemsById;
      if (!itemsById) return;
      const loaded = childIds
        .map((id) => itemsById[String(id)])
        .filter((item): item is Partial<ItemV2> => item !== undefined);
      if (loaded.length > 0) map.set(parentId, loaded);
    });
    return map;
  }, [expandedParents, results]);

  const loadingParentIds = useMemo(() => {
    if (expandedParents.length === 0) return EMPTY_LOADING;
    const loading = new Set<number>();
    expandedParents.forEach(({ parentId }, index) => {
      const result = results[index];
      if (result?.isFetching && result.data === undefined) loading.add(parentId);
    });
    return loading.size > 0 ? loading : EMPTY_LOADING;
  }, [expandedParents, results]);

  const isFetching = results.some((r) => r.isFetching);

  return { childrenByParentId, loadingParentIds, isFetching };
};
